import { Button, Icon, Text } from '@nike/eds';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setNposFilters } from '../../../../../redux/actions';
import { getBodyTranslations, getNposValue } from '../../../../../redux/selectors';
import { NPOS } from '../../../../../utils/constants';
import {
 getAdvancedFiltersCount, getFilterKeyById, getDefaultStateForMoreFilters, toNumber, isAnyFilterError,
 getFilterClassName
} from '../../nposUtils';
import { PlusIcon } from '../../reusable/icons';
import FilterRow from '../../reusable/Layout';
import Popup from '../../reusable/popup';

import AmountRange from './AmountRange';
import AthleteRegisterInfo from './AthleteRegisterInfo';
import KeywordsSearch from './KeywordsSearch';
import ModifierSelectors from './ModifierSelectors';
import O2OEvents from './O2OEvents';
import StatusSelectors from './StatusSelectors';
import TransactionInformation from './TransactionInformation';
import TransactionType from './TransactionType';

const INPUT_VALIDATIONS = {
  maximumAmount: toNumber,
  minimumAmount: toNumber,
};

const MoreFilters = ({ handleChange }) => {
  const [open, setOpen] = useState(false);

  const nposFilters = useSelector((state) => state.nposFilters);
  const nposErrors = useSelector((state) => state.nposErrors);
  const translations = useSelector(getBodyTranslations);

  const appliedAdvancedFiltersCnt = getAdvancedFiltersCount(nposFilters);
  const isFilterApplied = appliedAdvancedFiltersCnt > 0;

  const dispatch = useDispatch();

  const { openFilter } = useSelector(getNposValue);

  const onFilterValueChange = (e) => {
    const { id, value } = e.target || e;

    const convertedValue = INPUT_VALIDATIONS[id] ? INPUT_VALIDATIONS[id](value) : value;
    dispatch(setNposFilters({ [id]: convertedValue }));
    handleChange(getFilterKeyById(id), value);
  };

  const onFilterCommit = () => {
    setOpen(false);
  };

  const onClearFilter = () => {
    dispatch(setNposFilters(getDefaultStateForMoreFilters()));
    Object.entries(NPOS.FILTERS.MORE_FILTERS_CONFIG).map(([id, value]) => handleChange(getFilterKeyById(id), value?.defaultValue));
  };

  const Body = (
    <div className="d-sm-flx flx-dir-sm-c">
      <KeywordsSearch translations={translations} onFilterValueChange={onFilterValueChange} />
      <AmountRange translations={translations} onFilterValueChange={onFilterValueChange} />
      <AthleteRegisterInfo translations={translations} onFilterValueChange={onFilterValueChange} />
      <TransactionType translations={translations} onFilterValueChange={onFilterValueChange} />
      <TransactionInformation translations={translations} onFilterValueChange={onFilterValueChange} />
      <div>
        <section className="ncss-col-sm-6 pl0-sm">
          <ModifierSelectors translations={translations} onFilterValueChange={onFilterValueChange} />
        </section>
        <section className="ncss-col-sm-6">
          <StatusSelectors translations={translations} onFilterValueChange={onFilterValueChange} />
        </section>
      </div>
      <div />
      <O2OEvents translations={translations} onFilterValueChange={onFilterValueChange} />
    </div>
  );

  const Footer = (
    <div className="d-sm-flx flx-jc-sm-fe p2-sm" style={{ gap: '20px' }}>
      {!!appliedAdvancedFiltersCnt && (
        <Button
          className="pr2-sm"
          data-testid="more-filters-clear-button"
          size="small"
          variant="ghost"
          onClick={onClearFilter}
        >
          {translations.RL_ClearFilter}
        </Button>
      )}
      <Button
        data-testid="more-filters-done-button"
        size="small"
        variant="primary"
        onClick={onFilterCommit}
      >
        {`${translations.RL_Done} ${appliedAdvancedFiltersCnt ? `(${appliedAdvancedFiltersCnt})` : ''}`}
      </Button>
    </div>
  );

  return (
    <div className="mt-sm-10" style={{ zIndex: open ? 1000 : 0 }}>
      <div className="d-sm-flx">
        <Button
          disableRipple
          beforeSlot={
            isAnyFilterError(nposErrors, 'MORE') ? (
              <><Icon
                className="text-color-error"
                name="AlertCircleFilled"
                size="m"
              /><PlusIcon />
              </>
            ) : (
              <PlusIcon />
            )
          }
          className={getFilterClassName('MORE', openFilter, isFilterApplied, open)}
          data-testid="filter-MORE"
          style={{ minWidth: 'max-content' }}
          variant="secondary"
          onClick={() => setOpen(!open)}
        >
          <Text className="eds-type--subtitle-1">{translations.RL_More} {appliedAdvancedFiltersCnt ? `(${appliedAdvancedFiltersCnt})` : ''}</Text>
        </Button>
      </div>
      {open && (
        <FilterRow>
          <Popup
            body={Body}
            data-testid="more-filters-popup"
            footer={Footer}
            open={open}
            setOpen={setOpen}
            title={translations.RL_MoreDetails}
          />
        </FilterRow>
      )}
    </div>
  );
};

MoreFilters.propTypes = {
  handleChange: PropTypes.func.isRequired,
};

export default MoreFilters;
